import {Component, computed, EventEmitter, Input, Output, signal} from '@angular/core';
import {IUsedAiCredits} from "../../../../../store/reducers/used-ai-credits.reducer";
import {UserSubscription} from "../../../../../user/user-subscription";

@Component({
  selector: 'db-trial-plan-details',
  templateUrl: './trial-plan-details.component.html',
  styleUrl: './trial-plan-details.component.scss',
  standalone: false
})
export class TrialPlanDetailsComponent {
  @Input('percentageLeft') set freePlanPercentage(num: number) {
    this.planPercentageLeft.set(num);
  }

  @Input('usedAiCredits') set AiCredits(num: IUsedAiCredits) {
    if (!num) {
      return;
    }
    this.creditsLimit.set(num.planLimit || 0);
    this.creditsUsed.set(num.alreadyUsed || 0);
    this.additionalCredits.set(num.additionalCredits || 0);
  }

  @Input('daysUntilNext') set daysUntilNextPayment(num: number) {
    this.daysUntilNext.set(num);
  }

  @Input() set userSubscription(subscription: UserSubscription) {
    this._userSubscription = subscription;
    if (subscription?.editTotal !== undefined) {
      this.creditsLimit.set(subscription.editTotal);
    }
    if (subscription?.editUsed !== undefined) {
      this.creditsUsed.set(subscription.editUsed);
    }
  }
  get userSubscription(): UserSubscription {
    return this._userSubscription;
  }

  @Input() freeDaysCount!: number;
  @Input() companyName: string;
  @Output() onUpgrade: EventEmitter<void> = new EventEmitter();

  private _userSubscription: UserSubscription;

  planPercentageLeft = signal(0);
  daysUntilNext = signal(0);
  creditsLimit = signal(5);
  creditsUsed = signal(0);
  additionalCredits = signal(0);

  showFreePlanMessage = computed(() => {
    return this.daysUntilNext() === 1;
  });

  aiCreditsMessage = computed(() => {
    const limit = +this.creditsLimit();
    if (this.aiCreditsMode() === 'danger' && !this.showFreePlanMessage()) {
      return {
        showMessage: true,
        className: 'danger' as 'danger',
        title: 'You’ve used all your trial AI credits.',
        subtitle: `Get more and keep building with ${this.companyName} Pro.`
      };
    }

    if (this.aiCreditsMode() === 'warning' && !this.showFreePlanMessage()) {
      return {
        showMessage: true,
        className: 'warning' as 'warning',
        title: 'Seems you’re enjoying building with AI.',
        subtitle: 'Go Pro to get more credits & secure your site.'
      };
    }

    return {
      showMessage: false,
      className: this.aiCreditsMode(),
      title: 'You’ve used all your trial AI credits.',
      subtitle: `Get more and keep building with ${this.companyName} Pro.`
    };
  });

  aiCreditsMode = computed(() => {
    if (this.creditsUsed() === +this.creditsLimit() + +this.additionalCredits()) {
      return 'danger';
    }

    if (this.creditsUsed() >= (+this.creditsLimit() + +this.additionalCredits()) / 2) {
      return 'warning';
    }

    return 'purple-blue';
  });

  creditsUsedPercentage = computed(() => {
    const limit = this.creditsLimit() + +this.additionalCredits();
    const used = this.creditsUsed();
    return limit > 0 ? (used / limit) * 100 : 0;
  });
}
